import React from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

// The patient-facing modal for an ordering pause. Two variants:
//
//   notice  — informational, ordering still works. Acknowledged with "Got it",
//             and remembered per pause so it shows once rather than every visit.
//   blocked — checkout is closed, and the modal cannot be dismissed. No button,
//             no backdrop click, no Escape. Ordering is genuinely unavailable,
//             so letting a patient click past the reason only leads them to a
//             disabled Pay button with no explanation on screen.
//
// Copy is admin-authored free text and renders as escaped plain text with line
// breaks preserved. No dangerouslySetInnerHTML, no markdown, no HTML: this
// field must not become an injection surface.
export default function OrderingPauseModal({
  open,
  variant,
  title,
  message,
  onClose,
}) {
  const dismissable = variant === "notice";
  // Headless UI always calls onClose for a backdrop click or Escape, so a
  // no-op is how a dialog refuses to close.
  const handleClose = dismissable ? onClose : () => {};

  return (
    <Dialog open={open} onClose={handleClose} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-600/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-md sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div>
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="mt-3 text-center sm:mt-5">
                <DialogTitle
                  as="h3"
                  className="text-lg font-semibold leading-6 text-gray-900"
                >
                  {title}
                </DialogTitle>
                <p className="mt-3 text-sm text-gray-700 whitespace-pre-line">
                  {message}
                </p>
              </div>
            </div>
            {dismissable && (
              <div className="mt-5 sm:mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex w-full justify-center rounded-md border border-transparent bg-integria-green px-4 py-2 text-sm font-medium text-white hover:bg-integria-green/90 focus:outline-hidden focus:border-blue-400"
                >
                  Got it
                </button>
              </div>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
